import {
  ApplicationRef,
  ComponentFactoryResolver,
  Directive,
  EmbeddedViewRef,
  EventEmitter,
  HostBinding,
  Injector,
  Input,
  OnInit,
  Output,
  Self,
  Type
} from '@angular/core';
import * as GeoJSON from 'geojson';
import * as _ from 'lodash';
import { LngLat, Map, MapMouseEvent, Popup, PopupOptions } from 'mapbox-gl';
import { filter } from 'rxjs/operators';

import { LJAMapComponent } from './map';

export interface PopupsModel {
  /** Special property multiple for component to render when multiple clicked */
  multiple?: Type<PopupComponent>;

  /** Component to render for each layer keyed by Layer ID */
  [layerId: string]: Type<PopupComponent>;
}

export interface PopupComponent {
  /** GeoJSON feature (for single clicked feature) or array for multiple features clicked */
  data: GeoJSON.Feature | GeoJSON.Feature[];
}

@Directive({
  selector: 'lja-map[ljaPopups]'
})
export class LJAPopupsDirective implements OnInit {
  @HostBinding('class.lja-popups')
  cssClass = true;

  @Input('ljaPopups')
  popups: PopupsModel = {}; // tslint:disable-line
  @Input()
  popupOptions: PopupOptions;

  /** Emitted on popup close with GeoJSON feature bound to popup (or array for multiple features) */
  @Output()
  popupClose = new EventEmitter<GeoJSON.Feature | GeoJSON.Feature[]>();

  /** Emitted on popup open with GeoJSON feature bound to popup (or array for multiple features) */
  @Output()
  popupOpen = new EventEmitter<GeoJSON.Feature | GeoJSON.Feature[]>();

  constructor(
    private _appRef: ApplicationRef,
    private _componentFactoryResolver: ComponentFactoryResolver,
    private _injector: Injector,
    @Self() private _mapComponent: LJAMapComponent
  ) {}

  ngOnInit() {
    this._mapComponent.mapLoad
      .pipe(filter(m => !!m))
      .subscribe(map => map.on('click', evt => this._startPopup(map, evt)));
  }

  private async _startPopup(map: Map, { point, lngLat }: MapMouseEvent) {
    const allFeatures: any[] = map.queryRenderedFeatures(point);
    const popupLayers = _.keys(this.popups);
    const popupFeatures = allFeatures.filter(feat => _.includes(popupLayers, feat.layer.id));

    if (!popupFeatures.length) {
      return;
    }

    if (popupFeatures.length === 1) {
      // Only 1 feature or cluster

      const singleFeature = popupFeatures[0];

      // use feat geometry if Point, otherwise use click location
      const singleLocation =
        singleFeature.geometry.type === 'Point' ? singleFeature.geometry.coordinates : lngLat;

      const popupData = await mapFeaturePopupData(map, singleFeature);

      this._renderPopup(map, singleLocation, this.popups[singleFeature.layer.id], popupData);

      return;
    }

    if (popupFeatures.length > 1 && this.popups.multiple) {
      // Multiple features or clusters

      // outputs [Feature (for single), [Feature, Feature] (for cluster)]
      const multipleFeatures = await Promise.all(
        popupFeatures.map(async feat => await mapFeaturePopupData(map, feat))
      );
      const flattened = _.flatMap(multipleFeatures);

      this._renderPopup(map, lngLat, this.popups.multiple, flattened);

      return;
    }

    /**
     * Map single feature or feature cluster to GeoJSON style feature (or array)
     * that will be bound to `data` on the popup
     */
    async function mapFeaturePopupData(mapControl, feature) {
      if (feature.properties.cluster === true) {
        // Cluster

        const clusterSource: any = mapControl.getSource(feature.layer.source);

        const getClusterFeatures = new Promise((resolve, reject) => {
          clusterSource.getClusterLeaves(
            feature.properties.cluster_id,
            100,
            0,
            (err, leaves) => (err ? reject(err) : resolve(leaves))
          );
        });
        const clusterFeatures = await getClusterFeatures;

        return (<any>clusterFeatures).map(feat => ({
          ...feat,
          ..._.pick(feature, ['source', 'layer'])
        }));
      } else {
        // Not a cluster
        return _.pick(feature, ['type', 'geometry', 'properties', 'source', 'layer']);
      }
    }
  }

  /**
   * Add a popup to the map
   * @param map - Map control to add the popup
   * @param lngLat - Location to anchor the popup
   * @param Component - Component class to generate component instance and use as popup content
   * @param data - Feature data to bind to the component instance `data` property
   */
  private _renderPopup(
    map: Map,
    lngLat: LngLat,
    Component: Type<PopupComponent>,
    data: GeoJSON.Feature | GeoJSON.Feature[]
  ) {
    const popup = new Popup(this.popupOptions)
      .setDOMContent(this._createComponent(Component, data))
      .setLngLat(lngLat);

    popup.on('open', evt => this.popupOpen.emit(data));
    popup.on('close', evt => this.popupClose.emit(data));

    popup.addTo(map);
  }

  /** Create a component instance, attach to the Angular application, and return the container DOM element */
  private _createComponent(
    Component: Type<PopupComponent>,
    data: GeoJSON.Feature | GeoJSON.Feature[]
  ): HTMLElement {
    const componentFactory = this._componentFactoryResolver.resolveComponentFactory(Component);
    const componentRef = componentFactory.create(this._injector);
    (<PopupComponent>componentRef.instance).data = data;

    this._appRef.attachView(componentRef.hostView); // attach component to Angular

    return (componentRef.hostView as EmbeddedViewRef<any>).rootNodes[0];
  }
}
